/* RemixExport.js */

import React, { useCallback, useState } from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import Strings from '../../translations/Translations';
import { useAuth } from '../../context/auth/AuthContext';
import { MovieCreator } from '../../utils/MovieCreator';
import WAM from '../../services/WAM';
import PSO from '../../services/PSO';
import { IoArrowForward, IoDownloadOutline } from 'react-icons/io5';
import { toast } from 'react-toastify';

const MOVIE_MP4_BASE_URL = process.env.REACT_APP_MOVIE_MP4;
const timeout = (ms) => { return new Promise(resolve => setTimeout(resolve, ms)); }

export default function RemixExport({ label = Strings('export_to_mp4'), remixPars, size ='md', className = '' }) {
  const { authenticated } = useAuth();
  const [exporting, setExporting] = useState(false);
  const [movieReady, setMovieReady] = useState(false);
  const [movieDownloadUrl, setMovieDownloadUrl] = useState('');
  
  const [modalConfirmExport, setModalConfirmExport] = useState(false);
  const toggleModalConfirmExport = useCallback(() => {
    setModalConfirmExport(!modalConfirmExport);
  }, [setModalConfirmExport, modalConfirmExport]);

  const [modalExport, setModalExport] = useState(false);
  const toggleModalExport = useCallback(() => {
    setModalExport(!modalExport);
  }, [setModalExport, modalExport]);

  const createExported = useCallback(async ({ movieId, remixPars }) => {
    if (remixPars && remixPars.rid && remixPars.rti && remixPars.rde) {
      const un = authenticated.un;
      const al = authenticated.al;
      const { rid, rti, rde } = remixPars;
      const pars = { un, al, rid, mid: movieId, ti: rti, bo: encodeURIComponent(rde) };
      try {
        await PSO.createExported(pars);
      } catch (err) {
        toast.error(Strings('error_while_saving_exported_video'));
        console.error(err);
      }
    }
  }, [authenticated]);

  const checkVideo = useCallback(async (response) => {
    const un = authenticated.un;
    const al = authenticated.al;
    const { movieId, albumId } = response;
    let waitTime = 10, ready = false;

    if (response.movieMakeTime) { waitTime = parseInt(response.movieMakeTime); }

    do {
      await timeout(waitTime * 1000);
      try {
        const response = await WAM.movieStatus({ un, al, movieId, albumId });
        if (response && response.result === 'success' && response.movie_created) {
          ready = true;
        } else {
          waitTime = 5;
        }
      } catch (err) {
        console.error(err);
      }
    } while (!ready);

    if (ready) {
      setMovieDownloadUrl(MOVIE_MP4_BASE_URL.replace('{id}', movieId));
      setMovieReady(ready);
      setExporting(false);
      createExported({ movieId, remixPars });
      toast.success(Strings('movie_successfully_created'));
    }
  }, [setMovieReady, setMovieDownloadUrl, authenticated, remixPars, setExporting, createExported]);

  const exportVideo = useCallback(async () => {
    if (remixPars && remixPars.rid) {
      setExporting(true);
      const un = authenticated.un;
      const al = authenticated.al;
      const { rid } = remixPars;

      const response = await MovieCreator({ un, al, rid });
      if (response && response.returnV) {
        checkVideo(response);
      } else {
        toast.error(Strings('an_error_ocurred_while_exporting_your_movie'));
      }
    }
  }, [authenticated, checkVideo, remixPars, setExporting]);

  const downloadVideo = useCallback(() => {
    window.open(movieDownloadUrl);
  }, [movieDownloadUrl]);

  const cancelExport = useCallback(() => {
    window.location.reload();
  }, []);
  
  return (
    <>
      <Button
        className={`pb-btn-1 ${className}`}
        size={size}
        onClick={toggleModalConfirmExport}
      >
        {label}
      </Button>

      <Modal isOpen={modalConfirmExport}>
        <ModalHeader toggle={toggleModalConfirmExport}>
          {Strings('start_exporting')}
        </ModalHeader>
        <ModalBody>
          {Strings('start_exporting_text')}
        </ModalBody>
        <ModalFooter className='text-right'>
          <Button onClick={toggleModalConfirmExport}>
            {Strings('cancel')}
          </Button>
          <Button
            className='pb-btn-1'
            onClick={() => {
              toggleModalConfirmExport();
              toggleModalExport();
              exportVideo();
            }}
          >
            {Strings('start_export')}{' '}<IoArrowForward size='1.3em' />
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={modalExport}>
        <ModalHeader className='text-center'>
          {!movieReady && Strings('your_movie_is_being_created')}
          {movieReady && Strings('your_movie_is_ready')}
        </ModalHeader>
        <ModalBody className='text-center'>
          <div className='mb-2 mt-2'>
            <h3 className='mb-0'>"{remixPars && remixPars.rti}"</h3>
            <small>*{remixPars && remixPars.rstag}</small>
          </div>
          {!movieReady && <p>{Strings('dont_close_page')}</p>}
          {!movieReady && (
            <span className='loader-1'></span>
          )}
          {movieReady && (
            <Button onClick={downloadVideo} size='lg' className='pb-btn-1 mt-3'>
              {Strings('download')}{' '}<IoDownloadOutline size='1.3em' />
            </Button>
          )}
        </ModalBody>
        <ModalFooter className='text-right'>
          {exporting && (
            <Button color='danger' onClick={cancelExport}>
              {Strings('cancel')}
            </Button>
          )}
          {movieReady && (
            <Button color='secondary' onClick={toggleModalExport}>
              {Strings('close')}
            </Button>
          )}
        </ModalFooter>
      </Modal>
    </>
  );
}
